import { Clear } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ActionsPicker } from "../../helpers";
import i18next from "i18next";
import dayjs from "dayjs";

const PREFIX = "MarkerForm";

const classes = {
  markerFormContainer: `${PREFIX}-markerFormContainer`,
  markerFormTopBar: `${PREFIX}-markerFormTopBar`,
  visibleContainer: `${PREFIX}-visibleContainer`,
  datePickersContainer: `${PREFIX}-datePickersContainer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.markerFormContainer}`]: {
    minWidth: "350px",
    padding: "0.5rem 1rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  [`& .${classes.markerFormTopBar}`]: {
    display: "flex",
    justifyContent: "space-between",
  },
  [`& .${classes.visibleContainer}`]: {
    textAlign: "center",
    marginTop: "-0.5rem",
    marginBottom: "0.5rem",
  },
  [`& .${classes.datePickersContainer}`]: {
    display: "flex",
    justifyContent: "space-around",
    "& > div": {
      width: "40%",
    },
    marginBottom: "1rem",
    paddingLeft: "1.5rem",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      justifyContent: "space-between",
      height: "125px",
      padding: "0 0.5rem",
      "& > div": {
        width: "100%",
      },
    },
  },
}));

const initMarkerModal = {
  lat: "",
  lng: "",
  title: "",
  radius: 1000,
  visible: false,
  free: false,
  controlDate: false,
  startDate: null,
  endDate: null,
};

const MarkerForm = ({ onClose, saveMarker, flyTo }) => {
  const [markerData, setMarkerData] = useState(initMarkerModal);
  const [disableSave, setDisableSave] = useState(false);
  const { t } = useTranslation();

  const handleSaveMarker = () => {
    const { lat, lng, ...rest } = markerData;
    saveMarker({
      ...rest,
      position: { lat, lng },
    });
    onClose();
    flyTo([lat, lng]);
  };

  const validateFields = () => {
    return !(
      markerData.title.length > 0 &&
      markerData.radius > 0 &&
      markerData.lat.length > 0 &&
      markerData.lat >= -90 &&
      markerData.lat <= 90 &&
      markerData.lng.length > 0 &&
      markerData.lng >= -180 &&
      markerData.lng <= 180 &&
      (!markerData.controlDate ||
        (markerData.controlDate && markerData.startDate && markerData.endDate))
    );
  };

  return (
    <Root onClick={(e) => e.stopPropagation()}>
      <Paper elevation={12} className={classes.markerFormContainer}>
        <Box className={classes.markerFormTopBar}>
          <Typography variant="h6" className={classes.videoTitle}>
            {t("positions.addMarkerTitle")}
          </Typography>
          <Box
            onClick={() => {
              onClose();
            }}
            className={classes.clearIconPreview}
            sx={{ cursor: "pointer", marginTop: "0.2rem" }}
          >
            <Clear color="primary" />
          </Box>
        </Box>
        <Box>
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            label={t("positions.input")}
            value={markerData.title}
            onChange={(e) =>
              setMarkerData({ ...markerData, title: e.target.value })
            }
            id={`markerData-title`}
            name={`markerData-title`}
          />
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            label={t("positions.latitude")}
            value={markerData.lat}
            id={`markerData-lat`}
            name={`markerData-lat`}
            onChange={(e) =>
              setMarkerData({ ...markerData, lat: e.target.value })
            }
          />
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            label={t("positions.longitude")}
            value={markerData.lng}
            id={`markerData-lng`}
            name={`markerData-lng`}
            onChange={(e) =>
              setMarkerData({ ...markerData, lng: e.target.value })
            }
          />
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            label={t("positions.inputRadius")}
            value={markerData.radius}
            type="number"
            id={`markerData-radius`}
            name={`markerData-radius`}
            InputProps={{ inputProps: { min: 1 } }}
            onChange={(e) =>
              setMarkerData({ ...markerData, radius: e.target.valueAsNumber })
            }
          />
        </Box>
        <Box>
          <div className={classes.visibleContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  color={"primary"}
                  onChange={(e) =>
                    setMarkerData({ ...markerData, visible: e.target.checked })
                  }
                  checked={markerData.visible || false}
                />
              }
              label={t("myExpariences.form.visible")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  color={"primary"}
                  onChange={(e) =>
                    setMarkerData({ ...markerData, free: e.target.checked })
                  }
                  checked={markerData.free || false}
                />
              }
              label={t("myExpariences.form.free")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  color={"primary"}
                  onChange={(e) =>
                    setMarkerData({
                      ...markerData,
                      controlDate: e.target.checked,
                    })
                  }
                  checked={markerData.controlDate || false}
                />
              }
              label={t("myExpariences.form.accessByDates")}
            />
          </div>
          {markerData.controlDate && (
            <div className={classes.datePickersContainer}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={i18next.language.substring(0, 2)}
              >
                <DatePicker
                  disablePast
                  label={t("myExpariences.form.startDate")}
                  slots={{
                    actionBar: ActionsPicker,
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear", "cancel", "accept"],
                    },
                  }}
                  className={classes.marginBottomStyle}
                  onChange={(e) =>
                    setMarkerData({
                      ...markerData,
                      startDate: e ? dayjs(e).format("YYYY-MM-DD") : null,
                    })
                  }
                  onError={(err) =>
                    err ? setDisableSave(true) : setDisableSave(false)
                  }
                  value={
                    markerData.startDate ? dayjs(markerData.startDate) : null
                  }
                  sx={{
                    marginRight: "1rem",
                  }}
                />
                <DatePicker
                  disablePast
                  minDate={
                    markerData.startDate
                      ? dayjs(markerData.startDate).add(1, "day")
                      : null
                  }
                  label={t("myExpariences.form.endDate")}
                  slots={{
                    actionBar: ActionsPicker,
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ["clear", "cancel", "accept"],
                    },
                  }}
                  className={classes.marginBottomStyle}
                  onChange={(e) =>
                    setMarkerData({
                      ...markerData,
                      endDate: e ? dayjs(e).format("YYYY-MM-DD") : null,
                    })
                  }
                  onError={(err) =>
                    err ? setDisableSave(true) : setDisableSave(false)
                  }
                  value={markerData.endDate ? dayjs(markerData.endDate) : null}
                />
              </LocalizationProvider>
            </div>
          )}
        </Box>
        <Button
          color="primary"
          variant="contained"
          disabled={disableSave || validateFields()}
          onClick={(e) => {
            e.stopPropagation();
            handleSaveMarker();
          }}
        >
          {t("positions.save")}
        </Button>
      </Paper>
    </Root>
  );
};

export default MarkerForm;
